import React, { useRef, useMemo, useCallback, useEffect, useState } from "react";
import { Sprite, Container } from "@pixi/react";
import anime from "animejs";
import { useSelector } from "react-redux";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { getUITexture } from "../../../../assets";
import {
  selectRoyalCollectActive,
  selectFeatureSpinComplete,
  selectFeatureSpinActive,
  selectCriticalAnimationPlaying,
  selectRoyalCollectRespinsLeft,
} from "../../../../State/selectors";
import { selectRoyalCollectTransitionActive } from "../../../../State/Slices/animationsSlice";
import { useRoyalCollectControl } from "../../../../Contexts/RoyalCollectControlContext";

import store from "../../../../State/Store/store";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";
import { createButtonPressAnimation } from "../../../../utilities";
import { useAppActivity } from "../../../../Contexts/AppActivityContext";

const PLAY_BUTTON_CONFIG = {
  BASE_WIDTH: 170,
  BASE_HEIGHT: 170,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { deviceMultiplier: 1.65 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { deviceMultiplier: 1.2 },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { deviceMultiplier: 1.65 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { deviceMultiplier: 1.35 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { deviceMultiplier: 1.75 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { deviceMultiplier: 1.4 },
    },
    [PixiSettings.DEVICE.DESKTOP]: { deviceMultiplier: 0.9 },
  },
};

const PLAY_BUTTON_POSITION_CONFIG = {
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar) => toolbar.width / 2,
      y: (toolbar, buttonConfig, dimensions) => -dimensions.height / 10.5,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width / 1.65,
      y: (toolbar, buttonConfig, dimensions) =>
        -dimensions.height / 2 + buttonConfig.height / 2.0,
    },
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar) => toolbar.width / 2,
      y: (toolbar, buttonConfig, dimensions) => -dimensions.height / 10.5,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width / 1.65,
      y: (toolbar, buttonConfig, dimensions) =>
        -dimensions.height / 2 + buttonConfig.height / 2.0,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width / 1.6,
      y: (toolbar, buttonConfig, dimensions) =>
        toolbar.height - buttonConfig.height * 2.2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width / 1.6,
      y: (toolbar, buttonConfig) => toolbar.height - buttonConfig.height * 3.2,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.275,
    y: (toolbar, buttonConfig) => buttonConfig.width * 0.1,
  },
};

// Minimum gap between actions (ms) — mirrors ButtonPlay.jsx
const MIN_ACTION_GAP = 50;
let lastActionTime = 0;

/**
 * ButtonRoyalCollectPlay
 *
 * Play/Stop button shown during the Royal Collect feature.
 * Replaces the base game play button (which hides when RC is active).
 *
 * Behavior mirrors ButtonPlay.jsx:
 * - Ready state: shows play icon → click starts spin
 * - Spinning state: shows stop icon → click triggers manual stop
 * - Stop pending state: disabled (waiting for symbols to land)
 * - Hover: swap to hover texture + scale up (1.05×)
 * - Click: press animation + sound
 */
const ButtonRoyalCollectPlay = ({ toolbar }) => {
  const buttonRef = useRef(null);
  const hoverContainerRef = useRef(null);
  const hoverAnimRef = useRef(null);
  const isHoveredRef = useRef(false);

  // Track whether a stop has been dispatched (button disabled until spin finishes)
  const [stopDispatched, setStopDispatched] = useState(false);

  const { dimensions, uiSymbolSize, device, orientation, getResponsiveConfig, isMobileSmall } =
    useDimensions();
  const { handleSpin, handleManualStop } = useRoyalCollectControl();

  const royalCollectActive = useSelector(selectRoyalCollectActive);
  const royalCollectTransitionActive = useSelector(selectRoyalCollectTransitionActive);
  const featureSpinComplete = useSelector(selectFeatureSpinComplete);
  const featureSpinActive = useSelector(selectFeatureSpinActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const respinsLeft = useSelector(selectRoyalCollectRespinsLeft);
  const { registerSpin } = useAppActivity();

  // Derived state — mirrors ButtonPlay.jsx shouldShowStop logic
  const shouldShowStop = featureSpinActive && !featureSpinComplete;

  // Button is interactive when:
  // - Not in transition
  // - AND either ready for spin (featureSpinComplete + respins left) or spinning (for manual stop)
  // - AND stop hasn't been dispatched yet
  const canStartSpin = featureSpinComplete && respinsLeft > 0;
  const canManualStop = featureSpinActive && !stopDispatched;
  const isInteractive =
    !royalCollectTransitionActive &&
    !criticalAnimationPlaying &&
    (canStartSpin || canManualStop);

  const buttonConfig = useMemo(
    () => getResponsiveConfig(PLAY_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Reset stopDispatched when spin completes (ready for next spin)
  useEffect(() => {
    if (featureSpinComplete && !featureSpinActive) {
      setStopDispatched(false);
    }
  }, [featureSpinComplete, featureSpinActive]);

  // ── Icon Texture ──────────────────────────────────────────────────────
  // Play icon when ready, Stop icon when spinning (matches ButtonPlay.jsx)

  const iconTexture = getUITexture(shouldShowStop ? "icon_stop" : "icon_play");

  // ── Hover Effects ─────────────────────────────────────────────────────
  // Mirrors ButtonPlay.jsx: swap texture + scale container on hover

  const handlePointerOver = useCallback(() => {
    if (hoverContainerRef.current) {
      isHoveredRef.current = true;

      // Swap to hover texture
      if (buttonRef.current) {
        const hoverTex = getUITexture(
          shouldShowStop ? "icon_stop_hover" : "icon_play_hover",
        );
        if (hoverTex) buttonRef.current.texture = hoverTex;
      }

      if (hoverAnimRef.current) hoverAnimRef.current.pause();

      hoverAnimRef.current = anime({
        targets: hoverContainerRef.current.scale,
        x: 1.05,
        y: 1.05,
        duration: 150,
        easing: "easeOutQuad",
      });
    }
  }, [shouldShowStop]);

  const handlePointerOut = useCallback(() => {
    if (hoverContainerRef.current) {
      isHoveredRef.current = false;

      // Revert to normal texture
      if (buttonRef.current) {
        const normalTex = getUITexture(
          shouldShowStop ? "icon_stop" : "icon_play",
        );
        if (normalTex) buttonRef.current.texture = normalTex;
      }

      if (hoverAnimRef.current) hoverAnimRef.current.pause();

      hoverAnimRef.current = anime({
        targets: hoverContainerRef.current.scale,
        x: 1.0,
        y: 1.0,
        duration: 150,
        easing: "easeOutQuad",
      });
    }
  }, [shouldShowStop]);

  // Sync texture when shouldShowStop changes while cursor is already on the button
  // (e.g., user clicks Play → spin starts → icon should switch to stop_hover)
  useEffect(() => {
    if (buttonRef.current) {
      if (isHoveredRef.current) {
        const hoverTex = getUITexture(
          shouldShowStop ? "icon_stop_hover" : "icon_play_hover",
        );
        if (hoverTex) buttonRef.current.texture = hoverTex;
      }
    }
  }, [shouldShowStop]);

  // ── Action Handlers ───────────────────────────────────────────────────

  const onPointerDown = useCallback(() => {
    if (!isInteractive) return;

    // Rate limiting (matches ButtonPlay MIN_ACTION_GAP)
    const now = Date.now();
    if (now - lastActionTime < MIN_ACTION_GAP) return;
    lastActionTime = now;

    // Sound + press animation on every meaningful action
    soundManager.play(SOUND_NAME.button_click);
    if (buttonRef.current) {
      createButtonPressAnimation(buttonRef.current, {
        targetWidth: buttonConfig.width,
        targetHeight: buttonConfig.height,
      });
    }

    // During spin → manual stop; otherwise → new spin
    if (featureSpinActive && !featureSpinComplete) {
      setStopDispatched(true);
      handleManualStop();
    } else if (featureSpinComplete && respinsLeft > 0) {
      registerSpin(); // Reset idle timer — user is active
      handleSpin();
    }
  }, [
    isInteractive,
    featureSpinActive,
    featureSpinComplete,
    respinsLeft,
    handleManualStop,
    handleSpin,
    registerSpin,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  // ── Spacebar Support ──────────────────────────────────────────────────
  // Mirrors ButtonPlay.jsx spacebar handling for RC

  useEffect(() => {
    if (!royalCollectActive) return;

    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) return;
      if (event.repeat) return;

      event.preventDefault();

      // Rate limiting
      const now = Date.now();
      if (now - lastActionTime < MIN_ACTION_GAP) return;
      lastActionTime = now;

      // Read latest state to check if spin is allowed
      const state = store.getState();
      const rcActive = state.featureGame.royalCollectActive;
      const transitionActive = state.animations.royalCollectTransitionActive;

      if (rcActive && !transitionActive) {
        // Block during critical animations (collection in progress)
        if (state.animations.criticalAnimationPlaying) return;

        const isSpinning = state.spin.featureSpinActive;
        const spinDone = typeof state.spin.featureSpinComplete === 'boolean'
          ? state.spin.featureSpinComplete
          : state.spin.featureSpinComplete?.complete ?? state.spin.featureSpinComplete;

        // Sound + press animation
        soundManager.play(SOUND_NAME.button_click);
        if (buttonRef.current) {
          createButtonPressAnimation(buttonRef.current, {
            targetWidth: buttonConfig.width,
            targetHeight: buttonConfig.height,
          });
        }

        if (isSpinning && !spinDone) {
          // Currently spinning → manual stop
          setStopDispatched(true);
          handleManualStop();
        } else if (spinDone && state.featureGame.royalCollectRespinsLeft > 0) {
          // Ready for new spin (only if respins remain)
          registerSpin(); // Reset idle timer — user is active
          handleSpin();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [royalCollectActive, handleSpin, handleManualStop, registerSpin, buttonConfig.width, buttonConfig.height]);

  // ── Position ──────────────────────────────────────────────────────────

  const buttonPosition = useMemo(() => {
    const realDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;
    const deviceConfig = PLAY_BUTTON_POSITION_CONFIG[realDevice];
    const posConfig = deviceConfig?.[orientation] ?? deviceConfig;
    return {
      x: posConfig.x(toolbar, buttonConfig, dimensions, uiSymbolSize),
      y: posConfig.y(toolbar, buttonConfig, dimensions, uiSymbolSize),
    };
  }, [device, orientation, toolbar, buttonConfig, dimensions, uiSymbolSize, isMobileSmall]);

  if (!royalCollectActive) return null;

  return (
    <Container
      name="button_royal_collect_play"
      x={buttonPosition.x}
      y={buttonPosition.y}
    >
      {/* Hover container — scale animates here, independent of sprite press */}
      <Container ref={hoverContainerRef}>
        <Sprite
          ref={buttonRef}
          texture={iconTexture}
          name="button_royal_collect_play_icon"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode={isInteractive ? "static" : "none"}
          cursor={isInteractive ? "pointer" : "default"}
          alpha={isInteractive ? 1 : 0.5}
          resolution={2}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          pointerdown={onPointerDown}
        />
      </Container>
    </Container>
  );
};

export default ButtonRoyalCollectPlay;
